import { type IdToken, type User } from "@auth0/auth0-spa-js";
import { AUTH_CONNECTION } from "@web3auth/auth";
import { jwtDecode } from "jwt-decode";
import log from "loglevel";

import type { DashboardAuthUser } from "@/utils/interface";

import { getAuth0Connection, getVerifierForConnection } from "../auth0Connections";

type IdTokenClaims = IdToken & {
  email?: string;
  name?: string;
  nickname?: string;
  picture?: string;
};

function deriveVerifierId(auth0Connection: string, sub: string, email?: string): string {
  const normalizedEmail = (email ?? "").trim().toLowerCase();
  if (auth0Connection === "email" || sub.startsWith("email|")) {
    if (normalizedEmail) {
      return normalizedEmail;
    }
    return sub.split("|").slice(1).join("|").toLowerCase();
  }
  if (auth0Connection === "google-oauth2" || sub.startsWith("google-oauth2|")) {
    if (normalizedEmail) {
      return normalizedEmail;
    }
  }
  return sub.toLowerCase();
}

export function buildDashboardAuthUser(authConnection: string, user: User, claims: IdToken): DashboardAuthUser {
  const sub = claims.sub ?? user.sub ?? "";
  const auth0Connection = getAuth0Connection(authConnection);
  const verifier = getVerifierForConnection(authConnection);
  const email = user.email ?? (typeof claims.email === "string" ? claims.email : undefined);
  return {
    email,
    name: user.name ?? user.nickname,
    picture: user.picture,
    authConnection,
    verifier,
    verifierId: deriveVerifierId(auth0Connection, sub, email),
    sub,
  };
}

export function buildDashboardAuthUserFromIdToken(authConnection: string, idToken: string): DashboardAuthUser {
  const claims = jwtDecode<IdTokenClaims>(idToken);
  const user: User = {
    sub: claims.sub,
    email: typeof claims.email === "string" ? claims.email : undefined,
    name: typeof claims.name === "string" ? claims.name : undefined,
    nickname: typeof claims.nickname === "string" ? claims.nickname : undefined,
    picture: typeof claims.picture === "string" ? claims.picture : undefined,
  };
  return buildDashboardAuthUser(authConnection, user, { ...claims, __raw: idToken });
}

/** Infer auth connection from Auth0 `sub` when rehydrating without stored connection. */
export function inferAuthConnectionFromSub(sub: string): string | undefined {
  const prefix = sub.split("|")[0]?.toLowerCase();
  switch (prefix) {
    case "google-oauth2":
      return "google";
    case "github":
      return "github";
    case "apple":
      return "apple";
    case "email":
      return AUTH_CONNECTION.EMAIL_PASSWORDLESS;
    default:
      return undefined;
  }
}

/**
 * Resolve the auth connection from three signals, in trust order:
 *
 * 1. `trusted` — Auth0 appState. Bound to this specific login transaction (keyed by the OAuth
 *    state nonce), so it reflects the connection the user actually chose and cannot be clobbered
 *    by other tabs. We trust it as-is even if the sub mapping differs, because grouped/aggregated
 *    connections can legitimately share a sub prefix while resolving to different verifiers; a
 *    divergence is only logged, not overridden.
 * 2. `fallback` — the shared localStorage value mirrored in triggerLogin. Used when appState was
 *    lost (Safari ITP, etc.), but it is shared across tabs so it can be stale. The token's `sub`
 *    is derived from the token itself, so it wins whenever it maps to a known connection.
 * 3. `sub` inference — last resort when nothing explicit exists. Partial by design (unknown
 *    prefixes yield undefined), which is why it never replaces an explicit `trusted` value.
 */
export function resolveAuthConnection(trusted: string | undefined, fallback: string | undefined, sub: string | undefined): string | undefined {
  const inferred = sub ? inferAuthConnectionFromSub(sub) : undefined;
  if (trusted) {
    if (inferred && inferred !== trusted) {
      log.warn(`authConnection mismatch: appState "${trusted}" disagrees with token sub "${inferred}"; trusting appState.`);
    }
    return trusted;
  }
  if (inferred && fallback && inferred !== fallback) {
    log.warn(`authConnection mismatch: stored "${fallback}" disagrees with token sub "${inferred}"; using token sub.`);
  }
  return inferred ?? fallback;
}
